import type { CreativeDecisionProjection } from './contracts';

export const CREATIVE_PROVIDER_SOURCE_REPORT_MAX_KEYS = 100;

const MAX_CORRELATION_KEY_LENGTH = 200;
const MAX_PROVIDER_ID_LENGTH = 128;
const MAX_TEXT_LENGTH = 50_000;
const MAX_CAPABILITY_REASONS = 50;
const MAX_MEDIA_ITEMS = 16;
const SOURCE_ID = /^adsource_[a-f0-9]{32}$/;
const BINDING_ID = /^adsource_binding_[a-f0-9]{32}$/;
const SNAPSHOT_ID = /^adsnapshot_[a-f0-9]{40}$/;
const TAXONOMY_SNAPSHOT_ID = /^adsourcetaxonomy_[a-f0-9]{32}$/;
const SHA256 = /^[a-f0-9]{64}$/;

export interface ProviderSourceReportKey {
  correlationKey: string;
  provider: 'meta';
  providerAccountId: string;
  providerAdId?: string;
  providerCreativeId?: string;
}

export type ProviderSourceReportUnavailableReason =
  | 'binding_not_found'
  | 'binding_invalid'
  | 'provider_key_mismatch'
  | 'source_not_found'
  | 'source_invalid'
  | 'snapshot_not_found'
  | 'snapshot_invalid'
  | 'read_failed';

export interface ProviderSourceReportMedia {
  id: string;
  slotKey: string;
  kind: 'image' | 'video' | 'poster';
  url: string;
  mimeType: string;
  byteSize: number;
  sha256: string;
  width: number;
  height: number;
  durationMs?: number;
}

export interface ProviderSourceReportSource {
  id: string;
  provider: 'meta';
  providerAccountId: string;
  providerCreativeId: string;
  snapshotId: string;
  currentSnapshotId: string;
  isCurrentSnapshot: boolean;
  creativeName?: string;
  copy: {
    primaryText?: string;
    headline?: string;
    description?: string;
  };
  cta: {
    type?: string;
    destinationUrl?: string;
  };
  presentationState: 'exact_static' | 'candidate_set' | 'provider_generated_unknown';
  capabilityState: 'full_media' | 'partial_media' | 'metadata_only' | 'dynamic_manifest';
  capabilityReasons: string[];
  /** Provider evidence only. Consumers must not treat this as a deployment locale. */
  sourceLanguage: {
    locale?: string;
    origin: 'provider_declared' | 'user_confirmed' | 'ai_detected' | 'heuristic_detected' | 'unknown';
    confidence?: number;
    mixedLanguage: boolean;
  };
  deploymentCount: number;
  campaignCount: number;
  lastSyncedAt: string;
  taxonomy?: {
    state: 'not_analyzed' | 'suggested' | 'reviewed' | 'rejected' | 'stale';
    reviewRequired: boolean;
    taxonomySnapshotId?: string;
    sourceSnapshotId?: string;
    topic?: { label: string; detail: string };
    angle?: { label: string; detail: string };
    concept?: { label: string; detail: string };
    hook?: { label: string; detail: string };
    proof?: { label: string; detail: string };
    format?: { label: string; detail: string };
    confidence?: number;
    warnings?: string[];
  };
  media: {
    state: 'mirrored' | 'partial' | 'provider_reference_only';
    totalCount: number;
    mirroredCount: number;
    returnedCount: number;
    truncated: boolean;
    items: ProviderSourceReportMedia[];
  };
}

export interface ProviderSourceReportBinding {
  id: string;
  sourceSnapshotId: string;
  providerAdId: string;
  providerCreativeId: string;
  campaignId?: string;
  campaignName?: string;
  adSetId?: string;
  adSetName?: string;
  adName?: string;
  configuredStatus?: string;
  effectiveStatus?: string;
  firstObservedAt: string;
  latestObservedAt: string;
  latestMetadataSyncedAt: string;
  segmentlyAssociation?: {
    state: 'linked_in_window' | 'known_segmently_destination' | 'mixed' | 'not_linked' | 'unknown';
    qualifiesForSegmentlyMedia: boolean;
    evaluatedFrom: string;
    evaluatedTo: string;
    classifiedAt: string;
  };
}

export type ProviderSourceReportEnrichmentItem = ProviderSourceReportKey & (
  | {
    source: ProviderSourceReportSource;
    binding?: ProviderSourceReportBinding;
  }
  | {
    source: null;
    binding?: undefined;
    reason: ProviderSourceReportUnavailableReason;
  }
);

export interface ProviderSourceReportEnrichmentResult {
  items: ProviderSourceReportEnrichmentItem[];
}

/**
 * Maps report deployments to the smallest provider-key request. Unsupported
 * or incomplete rows are omitted so optional creative previews can never
 * block the report's metric rows.
 */
export function buildProviderSourceReportKeys(
  rows: readonly CreativeDecisionProjection[],
): ProviderSourceReportKey[] {
  const result: ProviderSourceReportKey[] = [];
  const seenCorrelationKeys = new Set<string>();
  for (const row of rows) {
    const deployment = row?.deployment;
    if (!deployment || deployment.provider !== 'meta') continue;
    const correlationKey = boundedIdentity(deployment.deploymentKey, MAX_CORRELATION_KEY_LENGTH);
    const providerAccountId = normalizedMetaAccountId(deployment.providerAccountId);
    if (!correlationKey || !providerAccountId || seenCorrelationKeys.has(correlationKey)) continue;

    // providerEntityId is an ad id only at explicit ad grain. We deliberately
    // do not parse providerResourceName or infer an ad id from another grain.
    const providerAdId = deployment.decisionUnitKind === 'ad'
      ? normalizedMetaId(deployment.providerEntityId)
      : undefined;
    const providerCreativeId = normalizedMetaId(deployment.providerCreativeOrAssetId);
    if (!providerAdId && !providerCreativeId) continue;

    seenCorrelationKeys.add(correlationKey);
    result.push(compact({
      correlationKey,
      provider: 'meta' as const,
      providerAccountId,
      providerAdId,
      providerCreativeId,
    }));
  }
  return result;
}

export function parseProviderSourceReportEnrichmentResult(
  value: unknown,
): ProviderSourceReportEnrichmentResult {
  const result = exactRecord(value, ['items'], 'provider source report enrichment result');
  if (!Array.isArray(result.items)
    || result.items.length === 0
    || result.items.length > CREATIVE_PROVIDER_SOURCE_REPORT_MAX_KEYS) {
    throw new Error(
      `Provider source report enrichment items must contain between 1 and ${CREATIVE_PROVIDER_SOURCE_REPORT_MAX_KEYS} entries.`,
    );
  }
  const seen = new Set<string>();
  return {
    items: result.items.map((entry, index) => {
      const field = `items[${index}]`;
      const raw = requiredRecord(entry, field);
      const isUnavailable = raw.source === null;
      const allowed = isUnavailable
        ? ['correlationKey', 'provider', 'providerAccountId', 'providerAdId', 'providerCreativeId', 'source', 'reason']
        : ['correlationKey', 'provider', 'providerAccountId', 'providerAdId', 'providerCreativeId', 'source', 'binding'];
      assertOnlyKeys(raw, allowed, field);
      const key = parseKey(raw, field);
      if (seen.has(key.correlationKey)) {
        throw new Error(`Duplicate provider source report correlation key: ${key.correlationKey}.`);
      }
      seen.add(key.correlationKey);

      if (isUnavailable) {
        return {
          ...key,
          source: null,
          reason: oneOf(raw.reason, [
            'binding_not_found',
            'binding_invalid',
            'provider_key_mismatch',
            'source_not_found',
            'source_invalid',
            'snapshot_not_found',
            'snapshot_invalid',
            'read_failed',
          ] as const, `${field}.reason`),
        };
      }

      const source = parseSource(raw.source, `${field}.source`);
      if (!key.providerCreativeId || key.providerCreativeId !== source.providerCreativeId
        || key.providerAccountId !== source.providerAccountId) {
        throw new Error(`${field} source identity does not match its provider key.`);
      }
      const binding = raw.binding === undefined
        ? undefined
        : parseBinding(raw.binding, `${field}.binding`);
      if (key.providerAdId) {
        if (!binding
          || binding.providerAdId !== key.providerAdId
          || binding.providerCreativeId !== source.providerCreativeId
          || binding.sourceSnapshotId !== source.snapshotId) {
          throw new Error(`${field} binding identity does not match its provider key and source.`);
        }
      } else if (binding) {
        throw new Error(`${field}.binding requires an explicit providerAdId.`);
      }
      return compact({ ...key, source, binding }) as ProviderSourceReportEnrichmentItem;
    }),
  };
}

function parseKey(value: Record<string, unknown>, field: string): ProviderSourceReportKey {
  const correlationKey = requiredBoundedString(
    value.correlationKey,
    `${field}.correlationKey`,
    MAX_CORRELATION_KEY_LENGTH,
  );
  if (value.provider !== 'meta') throw new Error(`${field}.provider must be meta.`);
  const providerAccountId = requiredMetaAccountId(value.providerAccountId, `${field}.providerAccountId`);
  const providerAdId = optionalMetaId(value.providerAdId, `${field}.providerAdId`);
  const providerCreativeId = optionalMetaId(value.providerCreativeId, `${field}.providerCreativeId`);
  if (!providerAdId && !providerCreativeId) {
    throw new Error(`${field} requires providerAdId or providerCreativeId.`);
  }
  return compact({
    correlationKey,
    provider: 'meta' as const,
    providerAccountId,
    providerAdId,
    providerCreativeId,
  });
}

function parseSource(value: unknown, field: string): ProviderSourceReportSource {
  const source = exactRecord(value, [
    'id',
    'provider',
    'providerAccountId',
    'providerCreativeId',
    'snapshotId',
    'currentSnapshotId',
    'isCurrentSnapshot',
    'creativeName',
    'copy',
    'cta',
    'presentationState',
    'capabilityState',
    'capabilityReasons',
    'sourceLanguage',
    'deploymentCount',
    'campaignCount',
    'lastSyncedAt',
    'taxonomy',
    'media',
  ], field);
  if (source.provider !== 'meta') throw new Error(`${field}.provider must be meta.`);
  const snapshotId = requiredPattern(source.snapshotId, SNAPSHOT_ID, `${field}.snapshotId`);
  const currentSnapshotId = requiredPattern(source.currentSnapshotId, SNAPSHOT_ID, `${field}.currentSnapshotId`);
  const isCurrentSnapshot = requiredBoolean(source.isCurrentSnapshot, `${field}.isCurrentSnapshot`);
  if (isCurrentSnapshot !== (snapshotId === currentSnapshotId)) {
    throw new Error(`${field}.isCurrentSnapshot does not match the snapshot identities.`);
  }
  return compact({
    id: requiredPattern(source.id, SOURCE_ID, `${field}.id`),
    provider: 'meta' as const,
    providerAccountId: requiredMetaAccountId(source.providerAccountId, `${field}.providerAccountId`),
    providerCreativeId: requiredMetaId(source.providerCreativeId, `${field}.providerCreativeId`),
    snapshotId,
    currentSnapshotId,
    isCurrentSnapshot,
    creativeName: optionalBoundedString(source.creativeName, `${field}.creativeName`, 1_000),
    copy: parseCopy(source.copy, `${field}.copy`),
    cta: parseCta(source.cta, `${field}.cta`),
    presentationState: oneOf(source.presentationState, [
      'exact_static',
      'candidate_set',
      'provider_generated_unknown',
    ] as const, `${field}.presentationState`),
    capabilityState: oneOf(source.capabilityState, [
      'full_media',
      'partial_media',
      'metadata_only',
      'dynamic_manifest',
    ] as const, `${field}.capabilityState`),
    capabilityReasons: requiredStringArray(
      source.capabilityReasons,
      `${field}.capabilityReasons`,
      MAX_CAPABILITY_REASONS,
      500,
    ),
    sourceLanguage: parseSourceLanguage(source.sourceLanguage, `${field}.sourceLanguage`),
    deploymentCount: requiredNonNegativeInteger(source.deploymentCount, `${field}.deploymentCount`),
    campaignCount: requiredNonNegativeInteger(source.campaignCount, `${field}.campaignCount`),
    lastSyncedAt: requiredIsoTimestamp(source.lastSyncedAt, `${field}.lastSyncedAt`),
    taxonomy: source.taxonomy === undefined
      ? undefined
      : parseTaxonomy(source.taxonomy, `${field}.taxonomy`),
    media: parseMediaProjection(source.media, `${field}.media`),
  });
}

function parseTaxonomy(
  value: unknown,
  field: string,
): NonNullable<ProviderSourceReportSource['taxonomy']> {
  const taxonomy = exactRecord(value, [
    'state',
    'reviewRequired',
    'taxonomySnapshotId',
    'sourceSnapshotId',
    'topic',
    'angle',
    'concept',
    'hook',
    'proof',
    'format',
    'confidence',
    'warnings',
  ], field);
  const state = oneOf(taxonomy.state, [
    'not_analyzed',
    'suggested',
    'reviewed',
    'rejected',
    'stale',
  ] as const, `${field}.state`);
  const reviewRequired = requiredBoolean(taxonomy.reviewRequired, `${field}.reviewRequired`);
  if (reviewRequired !== (state === 'suggested')) {
    throw new Error(`${field}.reviewRequired does not match its state.`);
  }
  const taxonomySnapshotId = taxonomy.taxonomySnapshotId === undefined
    ? undefined
    : requiredPattern(taxonomy.taxonomySnapshotId, TAXONOMY_SNAPSHOT_ID, `${field}.taxonomySnapshotId`);
  const sourceSnapshotId = taxonomy.sourceSnapshotId === undefined
    ? undefined
    : requiredPattern(taxonomy.sourceSnapshotId, SNAPSHOT_ID, `${field}.sourceSnapshotId`);
  const topic = optionalTaxonomyPair(taxonomy.topic, `${field}.topic`);
  const angle = optionalTaxonomyPair(taxonomy.angle, `${field}.angle`);
  const concept = optionalTaxonomyPair(taxonomy.concept, `${field}.concept`);
  const hook = optionalTaxonomyPair(taxonomy.hook, `${field}.hook`);
  const proof = optionalTaxonomyPair(taxonomy.proof, `${field}.proof`);
  const format = optionalTaxonomyPair(taxonomy.format, `${field}.format`);
  const hasProjection = Boolean(topic || angle || concept || hook || proof || format);
  if (hasProjection || taxonomySnapshotId || sourceSnapshotId) {
    if ((state !== 'suggested' && state !== 'reviewed')
      || !taxonomySnapshotId
      || !sourceSnapshotId
      || !topic
      || !angle
      || !concept
      || !hook
      || !proof
      || !format) {
      throw new Error(`${field} projected taxonomy must be complete and belong to a suggested or reviewed snapshot.`);
    }
  }
  return compact({
    state,
    reviewRequired,
    taxonomySnapshotId,
    sourceSnapshotId,
    topic,
    angle,
    concept,
    hook,
    proof,
    format,
    confidence: optionalFiniteNumber(taxonomy.confidence, `${field}.confidence`, 0, 1),
    warnings: taxonomy.warnings === undefined
      ? undefined
      : requiredStringArray(taxonomy.warnings, `${field}.warnings`, 20, 500),
  });
}

function optionalTaxonomyPair(
  value: unknown,
  field: string,
): { label: string; detail: string } | undefined {
  if (value === undefined) return undefined;
  const pair = exactRecord(value, ['label', 'detail'], field);
  return {
    label: requiredBoundedString(pair.label, `${field}.label`, 1_000),
    detail: requiredBoundedString(pair.detail, `${field}.detail`, 4_000),
  };
}

function parseCopy(value: unknown, field: string): ProviderSourceReportSource['copy'] {
  const copy = exactRecord(value, ['primaryText', 'headline', 'description'], field);
  return compact({
    primaryText: optionalBoundedString(copy.primaryText, `${field}.primaryText`, MAX_TEXT_LENGTH),
    headline: optionalBoundedString(copy.headline, `${field}.headline`, MAX_TEXT_LENGTH),
    description: optionalBoundedString(copy.description, `${field}.description`, MAX_TEXT_LENGTH),
  });
}

function parseCta(value: unknown, field: string): ProviderSourceReportSource['cta'] {
  const cta = exactRecord(value, ['type', 'destinationUrl'], field);
  return compact({
    type: optionalBoundedString(cta.type, `${field}.type`, 200),
    destinationUrl: optionalHttpUrl(cta.destinationUrl, `${field}.destinationUrl`),
  });
}

function parseSourceLanguage(
  value: unknown,
  field: string,
): ProviderSourceReportSource['sourceLanguage'] {
  const language = exactRecord(value, ['locale', 'origin', 'confidence', 'mixedLanguage'], field);
  return compact({
    locale: optionalBoundedString(language.locale, `${field}.locale`, 100),
    origin: oneOf(language.origin, [
      'provider_declared',
      'user_confirmed',
      'ai_detected',
      'heuristic_detected',
      'unknown',
    ] as const, `${field}.origin`),
    confidence: optionalFiniteNumber(language.confidence, `${field}.confidence`, 0, 1),
    mixedLanguage: requiredBoolean(language.mixedLanguage, `${field}.mixedLanguage`),
  });
}

function parseMediaProjection(
  value: unknown,
  field: string,
): ProviderSourceReportSource['media'] {
  const media = exactRecord(value, [
    'state',
    'totalCount',
    'mirroredCount',
    'returnedCount',
    'truncated',
    'items',
  ], field);
  if (!Array.isArray(media.items) || media.items.length > MAX_MEDIA_ITEMS) {
    throw new Error(`${field}.items must be an array with at most ${MAX_MEDIA_ITEMS} entries.`);
  }
  const items = media.items.map((entry, index) => parseMedia(entry, `${field}.items[${index}]`));
  const state = oneOf(media.state, [
    'mirrored',
    'partial',
    'provider_reference_only',
  ] as const, `${field}.state`);
  const totalCount = requiredNonNegativeInteger(media.totalCount, `${field}.totalCount`);
  const mirroredCount = requiredNonNegativeInteger(media.mirroredCount, `${field}.mirroredCount`);
  const returnedCount = requiredNonNegativeInteger(media.returnedCount, `${field}.returnedCount`);
  const truncated = requiredBoolean(media.truncated, `${field}.truncated`);
  if (returnedCount !== items.length
    || returnedCount > mirroredCount
    || mirroredCount > totalCount
    || truncated !== (mirroredCount > returnedCount)
    || (state === 'mirrored' && mirroredCount !== totalCount)) {
    throw new Error(`${field} count/state fields are inconsistent.`);
  }
  return { state, totalCount, mirroredCount, returnedCount, truncated, items };
}

function parseMedia(value: unknown, field: string): ProviderSourceReportMedia {
  const media = exactRecord(value, [
    'id',
    'slotKey',
    'kind',
    'url',
    'mimeType',
    'byteSize',
    'sha256',
    'width',
    'height',
    'durationMs',
  ], field);
  const kind = oneOf(media.kind, ['image', 'video', 'poster'] as const, `${field}.kind`);
  const durationMs = media.durationMs === undefined
    ? undefined
    : requiredPositiveInteger(media.durationMs, `${field}.durationMs`);
  if (kind === 'video' && durationMs === undefined) {
    throw new Error(`${field}.durationMs is required for video media.`);
  }
  return compact({
    id: requiredBoundedString(media.id, `${field}.id`, 500),
    slotKey: requiredBoundedString(media.slotKey, `${field}.slotKey`, 200),
    kind,
    url: requiredDurableMediaUrl(media.url, `${field}.url`),
    mimeType: requiredBoundedString(media.mimeType, `${field}.mimeType`, 200),
    byteSize: requiredPositiveInteger(media.byteSize, `${field}.byteSize`),
    sha256: requiredPattern(media.sha256, SHA256, `${field}.sha256`),
    width: requiredPositiveInteger(media.width, `${field}.width`),
    height: requiredPositiveInteger(media.height, `${field}.height`),
    durationMs,
  });
}

function parseBinding(value: unknown, field: string): ProviderSourceReportBinding {
  const binding = exactRecord(value, [
    'id',
    'sourceSnapshotId',
    'providerAdId',
    'providerCreativeId',
    'campaignId',
    'campaignName',
    'adSetId',
    'adSetName',
    'adName',
    'configuredStatus',
    'effectiveStatus',
    'firstObservedAt',
    'latestObservedAt',
    'latestMetadataSyncedAt',
    'segmentlyAssociation',
  ], field);
  return compact({
    id: requiredPattern(binding.id, BINDING_ID, `${field}.id`),
    sourceSnapshotId: requiredPattern(binding.sourceSnapshotId, SNAPSHOT_ID, `${field}.sourceSnapshotId`),
    providerAdId: requiredMetaId(binding.providerAdId, `${field}.providerAdId`),
    providerCreativeId: requiredMetaId(binding.providerCreativeId, `${field}.providerCreativeId`),
    campaignId: optionalBoundedString(binding.campaignId, `${field}.campaignId`, MAX_PROVIDER_ID_LENGTH),
    campaignName: optionalBoundedString(binding.campaignName, `${field}.campaignName`, 1_000),
    adSetId: optionalBoundedString(binding.adSetId, `${field}.adSetId`, MAX_PROVIDER_ID_LENGTH),
    adSetName: optionalBoundedString(binding.adSetName, `${field}.adSetName`, 1_000),
    adName: optionalBoundedString(binding.adName, `${field}.adName`, 1_000),
    configuredStatus: optionalBoundedString(binding.configuredStatus, `${field}.configuredStatus`, 100),
    effectiveStatus: optionalBoundedString(binding.effectiveStatus, `${field}.effectiveStatus`, 100),
    firstObservedAt: requiredIsoTimestamp(binding.firstObservedAt, `${field}.firstObservedAt`),
    latestObservedAt: requiredIsoTimestamp(binding.latestObservedAt, `${field}.latestObservedAt`),
    latestMetadataSyncedAt: requiredIsoTimestamp(
      binding.latestMetadataSyncedAt,
      `${field}.latestMetadataSyncedAt`,
    ),
    segmentlyAssociation: binding.segmentlyAssociation === undefined
      ? undefined
      : parseAssociation(binding.segmentlyAssociation, `${field}.segmentlyAssociation`),
  });
}

function parseAssociation(
  value: unknown,
  field: string,
): NonNullable<ProviderSourceReportBinding['segmentlyAssociation']> {
  const association = exactRecord(value, [
    'state',
    'qualifiesForSegmentlyMedia',
    'evaluatedFrom',
    'evaluatedTo',
    'classifiedAt',
  ], field);
  return {
    state: oneOf(association.state, [
      'linked_in_window',
      'known_segmently_destination',
      'mixed',
      'not_linked',
      'unknown',
    ] as const, `${field}.state`),
    qualifiesForSegmentlyMedia: requiredBoolean(
      association.qualifiesForSegmentlyMedia,
      `${field}.qualifiesForSegmentlyMedia`,
    ),
    evaluatedFrom: requiredIsoTimestamp(association.evaluatedFrom, `${field}.evaluatedFrom`),
    evaluatedTo: requiredIsoTimestamp(association.evaluatedTo, `${field}.evaluatedTo`),
    classifiedAt: requiredIsoTimestamp(association.classifiedAt, `${field}.classifiedAt`),
  };
}

function requiredRecord(value: unknown, field: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`${field} must be an object.`);
  }
  return value as Record<string, unknown>;
}

function exactRecord(value: unknown, allowed: readonly string[], field: string): Record<string, unknown> {
  const record = requiredRecord(value, field);
  assertOnlyKeys(record, allowed, field);
  return record;
}

function assertOnlyKeys(value: Record<string, unknown>, allowed: readonly string[], field: string): void {
  const unexpected = Object.keys(value).filter((key) => !allowed.includes(key));
  if (unexpected.length > 0) {
    throw new Error(`${field} contains unsupported fields: ${unexpected.join(', ')}.`);
  }
}

function requiredBoundedString(value: unknown, field: string, maxLength: number): string {
  if (typeof value !== 'string'
    || value.length === 0
    || value.length > maxLength
    || /[\u0000-\u001f\u007f]/.test(value)) {
    throw new Error(`${field} must be a bounded non-empty string.`);
  }
  return value;
}

function optionalBoundedString(
  value: unknown,
  field: string,
  maxLength: number,
): string | undefined {
  if (value === undefined) return undefined;
  return requiredBoundedString(value, field, maxLength);
}

function requiredPattern(value: unknown, pattern: RegExp, field: string): string {
  const result = requiredBoundedString(value, field, 500);
  if (!pattern.test(result)) throw new Error(`${field} has an invalid identity.`);
  return result;
}

function requiredMetaAccountId(value: unknown, field: string): string {
  const result = requiredBoundedString(value, field, MAX_PROVIDER_ID_LENGTH);
  if (!/^act_\d+$/.test(result)) throw new Error(`${field} must be a normalized Meta account id.`);
  return result;
}

function requiredMetaId(value: unknown, field: string): string {
  const result = requiredBoundedString(value, field, MAX_PROVIDER_ID_LENGTH);
  if (!/^\d+$/.test(result)) throw new Error(`${field} must be a numeric Meta id.`);
  return result;
}

function optionalMetaId(value: unknown, field: string): string | undefined {
  if (value === undefined) return undefined;
  return requiredMetaId(value, field);
}

function requiredBoolean(value: unknown, field: string): boolean {
  if (typeof value !== 'boolean') throw new Error(`${field} must be boolean.`);
  return value;
}

function requiredNonNegativeInteger(value: unknown, field: string): number {
  if (!Number.isInteger(value) || (value as number) < 0) {
    throw new Error(`${field} must be a non-negative integer.`);
  }
  return value as number;
}

function requiredPositiveInteger(value: unknown, field: string): number {
  if (!Number.isInteger(value) || (value as number) <= 0) {
    throw new Error(`${field} must be a positive integer.`);
  }
  return value as number;
}

function optionalFiniteNumber(
  value: unknown,
  field: string,
  min: number,
  max: number,
): number | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max) {
    throw new Error(`${field} must be a finite number between ${min} and ${max}.`);
  }
  return value;
}

function requiredIsoTimestamp(value: unknown, field: string): string {
  const result = requiredBoundedString(value, field, 100);
  if (!Number.isFinite(Date.parse(result))) throw new Error(`${field} must be an ISO timestamp.`);
  return result;
}

function optionalHttpUrl(value: unknown, field: string): string | undefined {
  if (value === undefined) return undefined;
  const result = requiredBoundedString(value, field, 4_096);
  let parsed: URL;
  try {
    parsed = new URL(result);
  } catch {
    throw new Error(`${field} must be a credential-free HTTP URL.`);
  }
  if (!['http:', 'https:'].includes(parsed.protocol) || parsed.username || parsed.password) {
    throw new Error(`${field} must be a credential-free HTTP URL.`);
  }
  return result;
}

function requiredDurableMediaUrl(value: unknown, field: string): string {
  const result = requiredBoundedString(value, field, 4_096);
  let parsed: URL;
  try {
    parsed = new URL(result);
  } catch {
    throw new Error(`${field} must be a durable credential-free HTTPS URL.`);
  }
  if (parsed.protocol !== 'https:'
    || parsed.hostname !== 'storage.googleapis.com'
    || parsed.username
    || parsed.password
    || parsed.search
    || parsed.hash) {
    throw new Error(`${field} must be a durable credential-free HTTPS URL.`);
  }
  return result;
}

function requiredStringArray(
  value: unknown,
  field: string,
  maxItems: number,
  maxLength: number,
): string[] {
  if (!Array.isArray(value) || value.length > maxItems) {
    throw new Error(`${field} must be an array with at most ${maxItems} entries.`);
  }
  return value.map((entry, index) => requiredBoundedString(entry, `${field}[${index}]`, maxLength));
}

function oneOf<const Values extends readonly string[]>(
  value: unknown,
  values: Values,
  field: string,
): Values[number] {
  if (typeof value !== 'string' || !values.includes(value)) {
    throw new Error(`${field} has an unsupported value.`);
  }
  return value as Values[number];
}

function boundedIdentity(value: unknown, maxLength: number): string | undefined {
  if (typeof value !== 'string') return undefined;
  const result = value.trim();
  return result
    && result.length <= maxLength
    && !/[\u0000-\u001f\u007f]/.test(result)
    ? result
    : undefined;
}

function normalizedMetaAccountId(value: unknown): string | undefined {
  const raw = boundedIdentity(value, MAX_PROVIDER_ID_LENGTH)?.replace(/^act_/, '');
  return raw && /^\d+$/.test(raw) ? `act_${raw}` : undefined;
}

function normalizedMetaId(value: unknown): string | undefined {
  const result = boundedIdentity(value, MAX_PROVIDER_ID_LENGTH);
  return result && /^\d+$/.test(result) ? result : undefined;
}

function compact<T extends Record<string, unknown>>(value: T): T {
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry !== undefined),
  ) as T;
}
